// @flow
import api from 'api'
import { getTeamRoles } from 'api/team'
import FormikSelectField from 'components/formik/FormikSelectField'
import FormikTextField from 'components/formik/FormikTextField'
import Loading from 'components/Loading'
import { Formik } from 'formik'
import usePermissions from 'hooks/usePermissions'
import { invalidateLoadUsers } from 'queries/users/useLoadUsers'
import * as React from 'react'
import { toast } from 'react-toastify'
import type { User } from 'types/user'
import Banner from 'ui/atoms/Banner'
import Button from 'ui/atoms/Button'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import Heading from 'ui/atoms/Heading'
import Modal from 'ui/atoms/Modal'
import Space from 'ui/atoms/Space'
import Text from 'ui/atoms/Text'
import { buildFullName } from 'utils/helpers'
import * as Yup from 'yup'

import PaperPlane from '../PaperPlane'

type Props = {
  onSubmit?: Function,
  onClose: Function,
  isEditing?: boolean,
  userToEdit?: ?User,
}

const schema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  email: Yup.string().email('Not a valid email').required('Email is required'),
  role: Yup.string().required('Role is required'),
})

function ModalAddTeamUser({
  onSubmit,
  onClose,
  isEditing,
  userToEdit,
}: Props): React.Node {
  const [isSubmitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState(false)
  const [teamRoles, setTeamRoles] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const { isAdminUser } = usePermissions()

  React.useEffect(() => {
    async function load() {
      setLoading(true)

      try {
        const result = await getTeamRoles('')
        setTeamRoles(
          result.filter(
            (role) =>
              role.is_assignable &&
              (isAdminUser || role.name !== 'Administrator')
          )
        )
      } catch {
        setError(true)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [isAdminUser])

  const onInviteUser = React.useCallback(
    async (first_name, last_name, email, role) => {
      try {
        const invitedUser = await api.inviteUser(
          first_name,
          last_name,
          email,
          role
        )

        invalidateLoadUsers()

        onClose()

        toast.success(
          `${
            buildFullName(invitedUser?.first_name, invitedUser?.last_name) ??
            'User'
          } successfully invited`
        )
        return true
      } catch (error) {
        if (
          error?.response?.data?.detail?.includes(
            'This email has already been registered.'
          )
        ) {
          toast.error('User already exists.')
        } else {
          toast.error(
            error.response.data.detail ?? 'Sorry, something went wrong'
          )
        }
        return false
      }
    },
    [onClose]
  )

  const onSendClick = React.useCallback(
    async ({ first_name, last_name, email, role }) => {
      setSubmitting(true)
      try {
        if (isEditing && onSubmit) {
          await onSubmit(first_name, last_name, email, role, userToEdit)
        } else {
          await onInviteUser(first_name, last_name, email, role)
        }
      } finally {
        setSubmitting(false)
      }
    },
    [isEditing, onInviteUser, onSubmit, userToEdit]
  )

  const roleOptions = React.useMemo(() => {
    const teamRoleOptions = teamRoles.map((customRole) => ({
      label: customRole.name,
      value: customRole.id.toString(),
    }))

    return teamRoleOptions
  }, [teamRoles])

  const initialValues = React.useMemo(() => {
    return {
      first_name: userToEdit?.first_name,
      last_name: userToEdit?.last_name,
      email: userToEdit?.email,
      role:
        userToEdit?.membership?.permissions_groups[0]?.id.toString() ||
        roleOptions[0]?.value,
    }
  }, [userToEdit, roleOptions])

  if (error) {
    return (
      <Modal closeButton show onHide={onClose}>
        <span>An error has occurred.</span>
      </Modal>
    )
  }

  if (loading) {
    return (
      <Modal closeButton show onHide={onClose}>
        <Loading skipLogoutMessage fullHeight={false} />
      </Modal>
    )
  }

  return (
    <Modal closeButton show onHide={onClose}>
      <Space size="large">
        <div>
          <Heading>{isEditing ? 'Edit Member' : 'Add Member'}</Heading>
          <Text $light>
            {isEditing
              ? 'Update the user information'
              : 'Add the new user information'}
          </Text>
        </div>
        <Formik
          initialValues={initialValues}
          onSubmit={onSendClick}
          validationSchema={schema}
        >
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <Space>
                <Flex $horizontalSpacing="medium">
                  <FlexBox>
                    <FormikTextField
                      label="First name"
                      name="first_name"
                      placeholder="First name"
                    />
                  </FlexBox>
                  <FlexBox>
                    <FormikTextField
                      label="Last name"
                      name="last_name"
                      placeholder="Last name"
                    />
                  </FlexBox>
                </Flex>
                <Flex $horizontalSpacing="medium">
                  <FlexBox>
                    <FormikTextField
                      disabled={isEditing}
                      label="Email"
                      name="email"
                      placeholder="Email"
                      type="email"
                    />
                  </FlexBox>
                  <FlexBox>
                    <FormikSelectField
                      semibold
                      label="Role"
                      name="role"
                      options={roleOptions}
                      placeholder="Role"
                    />
                  </FlexBox>
                </Flex>
                <Button
                  $primary
                  disabled={isSubmitting}
                  icon={!isEditing && <PaperPlane />}
                  loading={isSubmitting}
                  onClick={handleSubmit}
                  type="submit"
                >
                  {isEditing ? 'Save' : 'Send Invite'}
                </Button>
              </Space>
            </form>
          )}
        </Formik>
      </Space>

      {!isEditing && (
        <Banner>
          <Text $small>
            Please note: by inviting a new user, you are authorizing Zentake to
            charge an additional monthly subscription fee for the new user as
            outlined in your agreement.
          </Text>
        </Banner>
      )}
    </Modal>
  )
}

export default ModalAddTeamUser
